import {
    ICompanySyncstep,
    IExportAttribute,
    IMessageData,
    IObjectData,
    IObjectType,
    IRouteDefinitionParams,
    IRuleset,
    ISyncField
} from "../../../index";
import * as ko from "knockout";
import {ObjectTypeId} from "../../../constants";
import {IExportFlowMutable, IExportTemplateMutable} from "../index";
import {nsExportTemplate} from "../fn";
import {nsParentPath} from "./fn";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {createErrorMessage, getSyncFieldDisplayName} from "../../../util/misc";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {IRequestObject} from "../../../req/index";
import {nsRoute} from "../../../util/route";
import {Mutable} from "../mutable";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import createExportFlowMutable = nsExportTemplate.createExportFlowMutable;
import getType = nsParentPath.getType;
import getTypeLabel = nsParentPath.getTypeLabel;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import mutableHtml from './mutable.html?raw';

export let template: string = mutableHtml;

interface IParams extends IRouteDefinitionParams {
    exportflow: KnockoutObservableArray<IExportFlowMutable>
    exporttemplate: IExportTemplateMutable
    objecttype: KnockoutObservable<IObjectType>
    syncstep: KnockoutObservable<ICompanySyncstep>
    mutable: Mutable
}

export class viewModel extends ParsedRouteDataDecorator {

    private readonly ObjectTypeId = ObjectTypeId;
    private readonly TYPE = nsParentPath.TYPE;
    private readonly AVAILABLE_TYPES = nsParentPath.AVAILABLE_TYPES;

    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    isReady: KnockoutObservable<boolean> = ko.observable(false);

    private _objectSyncFields!: ISyncField[];
    private _departmentSyncFields!: ISyncField[];
    private _exportAttributes!: IExportAttribute[];

    // array for storing parent path export flows
    exportflows: KnockoutObservableArray<ParentPathExportFlowMutable> = ko.observableArray<ParentPathExportFlowMutable>([]);
    objecttype: KnockoutObservable<IObjectType>;

    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private reqGetSyncFields: IRequestObject | null = null;
    private readonly reqGetSyncFieldsDep: IRequestObject = nsSyncFields.getSyncFields(ObjectTypeId.Department);
    private reqobj_getexportattributes!: IRequestObject<IExportAttribute[]>;

    constructor(private params: IParams) {
        super(params);
        this.objecttype = params.objecttype;

        this.kosub.subscribe(this.exportflows, () => {
            this.messageData(this.exportflows().length
                ? null
                : {message: `no data`}
            );
        });

        // Create keeps this panel mounted before objecttype/syncstep are chosen — wait for both.
        const onObjectTypeOrSyncstepChange = () => this.initRequest();
        this.kosub.subscribe(this.objecttype, onObjectTypeOrSyncstepChange);
        this.kosub.subscribe(this.params.syncstep, onObjectTypeOrSyncstepChange);
        this.initRequest();
    }

    private initRequest() {
        const objecttype = this.objecttype();
        const syncstep = this.params.syncstep();
        if (!objecttype || !syncstep) {
            this.isReady(false);
            return;
        }

        this.messageData({message: REQUEST_MESSAGE});
        this.reqGetSyncFields?.promise.abort();
        this.reqGetSyncFields = nsSyncFields.getSyncFields(objecttype.id);

        Promise.resolve()
            .then(() => {
                return this.reqGetSyncFields!.request
                    .then((data: ISyncField[]) => {
                        this._objectSyncFields = data;
                    });
            })
            .then(() => {
                return this.reqGetSyncFieldsDep.request
                    .then((data: ISyncField[]) => {
                        this._departmentSyncFields = data;
                    });
            })
            .then(() => {
                this.reqobj_getexportattributes?.promise.abort()
                this.reqobj_getexportattributes = nsExportTemplate.getExportAttributes(syncstep.syncstep.id, objecttype.id);
                return this.reqobj_getexportattributes.request
                    .then((data: IExportAttribute[]) => {
                        this._exportAttributes = data;
                    });
            })
            .then(() => {
                // retrieve existing exportflows
                this.exportflows(this.params.exportflow()
                    .filter(nsExportTemplate.isExportFlowMutableParentPath)
                    .map((efm: IExportFlowMutable) => new ParentPathExportFlowMutable(efm, this.params.mutable))
                );
            })
            .then(() => {
                this.isReady(true);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
    }

    hasMultiple = ko.pureComputed((): boolean => this.exportflows().length > 1);

    usedRulesets = ko.pureComputed((): IRuleset[] => {
        if (this.exportflows().length) {
            return this.exportflows()
                .map((item: ParentPathExportFlowMutable) => item.efm.ruleset())
                .filter((ruleset): ruleset is IRuleset => !!ruleset);
        } else {
            return [];
        }
    });

    onAddClick() {
        const efm = createExportFlowMutable();
        this.params.mutable.trackFlowErrors(efm, 'synctemplateparentpath')
        const exportAttribute = this._exportAttributes.find((item: IExportAttribute) => item.name == "parentPath");
        efm.targetattribute(exportAttribute);
        efm.sourceattribute("");

        const ppefm = new ParentPathExportFlowMutable(efm, this.params.mutable);

        this.params.exportflow.push(ppefm.efm);
        this.exportflows.push(ppefm);
    }

    onRemoveClick(ppefm: ParentPathExportFlowMutable) {
        this.params.mutable.untrackFlowErrors(ppefm.efm)
        this.params.exportflow.remove(ppefm.efm);
        this.exportflows.remove(ppefm);
    }

    override dispose() {
        this.kosub.unsuscribeAll();
        this.exportflows().forEach((item: ParentPathExportFlowMutable) => item.dispose());
        this.reqGetSyncFields?.promise.abort();
        this.reqGetSyncFieldsDep.promise.abort();
        this.reqobj_getexportattributes?.promise.abort()
    }
}

class ParentPathExportFlowMutable {

    kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    constructor(public efm: IExportFlowMutable, private mutable: Mutable) {
        const type = getType(efm.expression(), efm.sourceattribute());
        this.efm.type(type);

        this.kosub.subscribe(this.efm.type, () => {
            // let the view update itself with its select menus - then reset the sourceattribute
            setTimeout(() => this.efm.sourceattribute(null));
        });
    }

    typeLabel = ko.pureComputed(() => getTypeLabel(this.efm.expression(), this.efm.sourceattribute()));

    private getDepartmentText(obj: IObjectData) {
        return getSyncFieldDisplayName(obj.type.id, obj.syncfields);
    }

    dispose() {
        this.kosub.unsuscribeAll();
    }

}
